import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { TestResults } from '../types';
import { HomeIcon, ListIcon, RefreshCwIcon, ShareIcon, DownloadIcon } from 'lucide-react';
import { calculateBruteNerdIdentity } from '../utils/scoring';
import { shareResults } from '../utils/shareImage';
interface ResultsScreenProps {
  results: TestResults;
  onHome: () => void;
  onViewLeaderboard: () => void;
  onChallengeAgain: () => void;
}
export function ResultsScreen({
  results,
  onHome,
  onViewLeaderboard,
  onChallengeAgain
}: ResultsScreenProps) {
  const identity = calculateBruteNerdIdentity(results.bruteScore, results.nerdTimeSeconds);
  const [sharing, setSharing] = useState(false);
  const handleShare = async () => {
    setSharing(true);
    try {
      await shareResults({
        firstName: results.firstName,
        lastName: results.lastName,
        bruteScore: results.bruteScore,
        nerdTimeSeconds: results.nerdTimeSeconds,
        identity
      });
    } catch (e) {
      console.error('Share failed', e);
    } finally {
      setSharing(false);
    }
  };
  return <div className="min-h-screen bg-zinc-950 p-4 md:p-8 flex items-center justify-center" data-id="element-95">
      <div className="w-full max-w-4xl" data-id="element-96">
        <button onClick={onHome} className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors mb-8 text-sm" data-id="element-97">
          <HomeIcon className="w-4 h-4" data-id="element-98" /> Back to Home
        </button>

        <div className="text-center mb-10" data-id="element-99">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-4 uppercase italic" data-id="element-100">
            CHALLENGE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-cyan-500" data-id="element-101">
              COMPLETE
            </span>
          </h1>
          <p className="text-xl text-gray-400" data-id="element-102">
            Great job, {results.firstName}!
          </p>
        </div>

        {/* Identity Result */}
        <div className="mb-8 bg-zinc-900/60 border border-zinc-700 rounded-xl p-6 md:p-8 text-center" data-id="element-103">
          <p className="text-gray-500 text-xs uppercase tracking-widest mb-2 font-bold" data-id="element-104">
            Your Identity
          </p>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-1 uppercase tracking-tight" data-id="element-105">
            {identity.label}
          </h2>
          <p className="text-gray-400 text-sm md:text-base mb-8 max-w-md mx-auto" data-id="element-106">
            {identity.description}
          </p>

          {/* Split bar */}
          <div className="relative h-10 rounded-full overflow-hidden bg-zinc-800 mb-4" data-id="element-107">
            <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-600 to-orange-500 flex items-center justify-start pl-4" style={{
            width: `${identity.brutePercent}%`
          }} data-id="element-108">
              {identity.brutePercent > 20 && <span className="text-white font-black text-sm uppercase tracking-wider" data-id="element-109">
                  {identity.brutePercent}% Brute
                </span>}
            </div>
            <div className="absolute right-0 top-0 h-full bg-gradient-to-l from-cyan-600 to-cyan-500 flex items-center justify-end pr-4" style={{
            width: `${identity.nerdPercent}%`
          }} data-id="element-110">
              {identity.nerdPercent > 20 && <span className="text-white font-mono font-bold text-sm" data-id="element-111">
                  {identity.nerdPercent}% Nerd
                </span>}
            </div>
          </div>
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider" data-id="element-112">
            <span className="text-orange-500" data-id="element-113">💪 BRUTE</span>
            <span className="text-cyan-500 font-mono" data-id="element-114">NERD 🧠</span>
          </div>
        </div>

        {/* Score cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-115">
          <Card variant="brawn" data-id="element-116">
            <div className="text-center" data-id="element-117">
              <h2 className="text-xl font-black text-orange-500 mb-2 uppercase italic" data-id="element-118">
                BRUTE SCORE
              </h2>
              <div className="text-5xl font-black text-white mb-2" data-id="element-119">
                {results.bruteScore}
              </div>
              <p className="text-orange-200/60 font-bold text-sm" data-id="element-120">
                LBS GRIP STRENGTH
              </p>
            </div>
          </Card>

          <Card variant="brains" data-id="element-121">
            <div className="text-center" data-id="element-122">
              <h2 className="text-xl font-mono font-bold text-cyan-500 mb-2" data-id="element-123">
                NERD SCORE
              </h2>
              <div className="text-5xl font-mono font-bold text-white mb-2" data-id="element-124">
                {results.nerdTimeSeconds.toFixed(1)}s
              </div>
              <p className="text-cyan-400/60 font-mono text-sm" data-id="element-125">
                {results.swipeCorrect}/{results.swipeTotal} correct · time to
                complete
              </p>
            </div>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-3 max-w-sm mx-auto" data-id="element-126">
          <Button onClick={onViewLeaderboard} variant="primary" size="lg" className="w-full flex items-center justify-center gap-2" data-id="element-127">
            <ListIcon className="w-5 h-5" data-id="element-128" /> SEE LEADERBOARD
          </Button>
          <Button onClick={handleShare} variant="brains" size="lg" className="w-full flex items-center justify-center gap-2" disabled={sharing} data-id="element-129">
            <ShareIcon className="w-5 h-5" data-id="element-130" />{' '}
            {sharing ? 'GENERATING...' : 'SHARE RESULTS'}
          </Button>
          <Button onClick={onChallengeAgain} variant="outline" size="lg" className="w-full flex items-center justify-center gap-2" data-id="element-131">
            <RefreshCwIcon className="w-5 h-5" data-id="element-132" /> CHALLENGE AGAIN
          </Button>
        </div>
      </div>
    </div>;
}